
import * as _ from "lodash"
import Vue from "vue"
import Component from "vue-class-component"
import {Inject} from "vue-property-decorator"
import {router} from "@/router"
import {RouteConfig} from "vue-router"
import {staticRoutes} from "@/staticRoutes"
import {getStaffRoutes} from "@/staffRoutes"
import {PlanService} from "@/superannuation/PlanService"

import {
  AuthenticationRequiredReason,
  AuthenticationService,
} from "./AuthenticationService"

@Component({
  router,
})
export default class App extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  @Inject()
  planService: PlanService

  // Represents the state of the client, based on client side login/logout. It
  // might not be in sync with the server side state. While the application is
  // loading it will be null, this is useful for the router so it is able to
  // handle the scenario where it's being re-loaded and the it is already
  // authenticated in the backend.
  isAuthenticated: boolean | null = null

  isServerUnavailable = false

  isLoading = true

  // The BC thumbnail generator for the reports will pass this flag
  isPreview = false

  routes: Array<RouteConfig> = []

  left = true

  constructor() {
    super()

    AuthenticationService.addAuthenticationInterceptor()
  }

  async created() {
    Vue.prototype.$app = this

    // Read params from the URL during created and set the mode
    this.isPreview = !_.isEmpty(this.$route.query.preview)

    if (this.$route.query.disableAnimations === "true") {
      document.body.classList.add("disable-animations")
    }

    this.addRoutes(staticRoutes)

    AuthenticationService.addAuthenticationRequiredListener(
      this.authenticationRequiredListener
    )

    console.log("registering listener event:authenticated")
    document.addEventListener(
      "event:authenticated",
      this.onAuthenticated,
      false
    )

    console.log("App created")

    this.isAuthenticated = await this.isApiAuthenticated()
  }

  async isApiAuthenticated(): Promise<boolean> {
    const isAuthenticated = await this.authenticationService.isAuthenticated()
    if (isAuthenticated) {
      this.resetRoutes()
      const isStaff = await this.authenticationService.isStaff()
      if (isStaff) {
        const isAdmin = await this.authenticationService.isAdmin()
        this.addRoutes(getStaffRoutes(isAdmin))
      }
    }

    return isAuthenticated
  }

  private async onAuthenticated() {
    console.log("Performing onAuthenticated")

    this.$root.$emit("event:authenticated")

    this.isAuthenticated = true

    this.$router.push("!redirect").catch(() => {
      // TODO do it in a cleaner way? raise an event instead?
      // Expected it to fail since redirect will change the route.
    })

    this.isLoading = true
  }

  private resetServices() {
    // Reset any stateful service so any sensitive or member specific
    // information can be discarded

    const provided = (<any>this.$root)._provided

    Object.keys(provided).forEach(key => {
      const entry = provided[key]
      console.log("Provided entry", entry)
      if (entry.reset != null) {
        entry.reset()
      }
    })
  }

  private async goToLogin() {
    await this.$router.push("/login")
  }

  /*
    private async preFetchReport() {
      const report = this.reportService.getDefaultReport(
        await this.memberService.getMember()
      )

      console.log("Pre-fetching default report")

      report.download().catch((reason: any) => {
        console.error("Error while pre-fetching report", reason)
      })

      this.$reportModal.report = report
    }
  */

  // private async setupDynamicRoutes() {
  //   // TODO load based on DF
  //   const superRoutes = await import(<any> "@/superRoutes")
  //   console.log("Adding dynamic routes:", superRoutes.default)
  //   this.addRoutes(superRoutes.default)
  // }

  private addRoutes(routes: Array<RouteConfig>) {
    this.$router.addRoutes(routes)
    this.routes.push(...routes)
  }

  private resetRoutes() {
    // Dynamic routes can't be removed from the $router
    // #see https://github.com/vuejs/vue-router/issues/1234
    // TODO Mark them as disabled?
    this.routes.splice(0)
    this.routes.push(...staticRoutes)
  }

  /**
   * This should only be called for user initiated logout, because it will log
   * the logout activity. DO NOT use it to force re-authentication
   */
  private async logout() {
    // Login page forces a logout
    await this.goToLogin()
  }

  private async authenticationRequiredListener(
    reason: AuthenticationRequiredReason
  ) {
    console.log("App processing - authentication-required", reason)

    if (this.isAuthenticated === true) {
      // Just clean up if has ever authenticated, to avoid reset on first load
      this.resetServices()
    }

    this.isAuthenticated = false

    this.resetRoutes()

    this.$root.$emit("event:authentication-required")

    this.isServerUnavailable = reason === "SERVER_UNAVAILABLE"

    if (!(<boolean>this.$route.meta.unauthenticatedAccess)) {
      if (reason === "LOGOUT") {
        await this.goToLogin()
      } else {
        await this.$router.push({
          path: "/login",
          query: {redirect: this.$route.path},
        })
      }
    }

    this.isLoading = false
  }
}
